import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  IndexTable,
  Spinner,
  Banner,
  Button,
  Modal,
  useBreakpoints,
} from '@shopify/polaris';
import { formatCurrency } from '../utils/memoizedFormatters';
import apiClient from '../utils/api';

/**
 * Weight-based shipping pricing table for BoxC services
 * Replaces the old ShippingMethodSelector with a structured pricing matrix
 */
const ShippingPricingTable = ({
  brand,
  onSave,
  loading = false,
  disabled = false
}) => {
  const { smUp } = useBreakpoints();

  // Define weight ranges - 1lb intervals for 0-10, 2lb for 10-20, 5lb for 20-50
  const weightRanges = useMemo(() => [
    // 0-10 lb: 1-lb intervals
    { id: '0-1', range: '0-1 lb', kg: '(0-0.45 kg)', min: 0, max: 1 },
    { id: '1-2', range: '1-2 lb', kg: '(0.45-0.91 kg)', min: 1, max: 2 },
    { id: '2-3', range: '2-3 lb', kg: '(0.91-1.36 kg)', min: 2, max: 3 },
    { id: '3-4', range: '3-4 lb', kg: '(1.36-1.81 kg)', min: 3, max: 4 },
    { id: '4-5', range: '4-5 lb', kg: '(1.81-2.27 kg)', min: 4, max: 5 },
    { id: '5-6', range: '5-6 lb', kg: '(2.27-2.72 kg)', min: 5, max: 6 },
    { id: '6-7', range: '6-7 lb', kg: '(2.72-3.18 kg)', min: 6, max: 7 },
    { id: '7-8', range: '7-8 lb', kg: '(3.18-3.63 kg)', min: 7, max: 8 },
    { id: '8-9', range: '8-9 lb', kg: '(3.63-4.08 kg)', min: 8, max: 9 },
    { id: '9-10', range: '9-10 lb', kg: '(4.08-4.54 kg)', min: 9, max: 10 },
    // 10-20 lb: 2-lb intervals
    { id: '10-12', range: '10-12 lb', kg: '(4.54-5.44 kg)', min: 10, max: 12 },
    { id: '12-14', range: '12-14 lb', kg: '(5.44-6.35 kg)', min: 12, max: 14 },
    { id: '14-16', range: '14-16 lb', kg: '(6.35-7.26 kg)', min: 14, max: 16 },
    { id: '16-18', range: '16-18 lb', kg: '(7.26-8.16 kg)', min: 16, max: 18 },
    { id: '18-20', range: '18-20 lb', kg: '(8.16-9.07 kg)', min: 18, max: 20 },
    // 20-50 lb: 5-lb intervals
    { id: '20-25', range: '20-25 lb', kg: '(9.07-11.34 kg)', min: 20, max: 25 },
    { id: '25-30', range: '25-30 lb', kg: '(11.34-13.61 kg)', min: 25, max: 30 },
    { id: '30-35', range: '30-35 lb', kg: '(13.61-15.88 kg)', min: 30, max: 35 },
    { id: '35-40', range: '35-40 lb', kg: '(15.88-18.14 kg)', min: 35, max: 40 },
    { id: '40-45', range: '40-45 lb', kg: '(18.14-20.41 kg)', min: 40, max: 45 },
    { id: '45-50', range: '45-50 lb', kg: '(20.41-22.68 kg)', min: 45, max: 50 },
  ], []);

  // Define the three BoxC services (IDs match database shippingMethod values)
  const services = useMemo(() => [
    { id: 'BoxC Parcel', name: 'BoxC Parcel', description: 'Standard delivery service' },
    { id: 'BoxC Plus', name: 'BoxC Plus', description: 'Enhanced delivery service' },
    { id: 'BoxC Priority', name: 'BoxC Priority', description: 'Priority delivery service' },
  ], []);

  // Pricing data state - structure: { serviceId: { weightRangeId: price } }
  const [pricingData, setPricingData] = useState({});
  const [originalPricingData, setOriginalPricingData] = useState({}); // Track original data for diff
  const [editingCell, setEditingCell] = useState(null); // { serviceId, weightRangeId }
  const [tempValues, setTempValues] = useState({});
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [overrideKeys, setOverrideKeys] = useState(new Set()); // Track which cells are brand overrides
  const [isDefaultMode, setIsDefaultMode] = useState(false); // Whether editing global defaults
  const [showResetModal, setShowResetModal] = useState(false); // Reset confirmation modal
  const [resetting, setResetting] = useState(false); // Loading state for reset

  // Load existing pricing data from API
  const loadPricingData = useCallback(async () => {
    try {
      setLoadingData(true);
      setLoadError(null);

      const brandIdParam = brand?.id || "default";
      const response = await apiClient.request(`/brands/${brandIdParam}/shipping-pricing`);

      if (response.success && response.pricingData) {
        // Use API data if available, otherwise fall back to defaults
        setPricingData(response.pricingData);
        // Store deep copy of original data for diff comparison
        setOriginalPricingData(JSON.parse(JSON.stringify(response.pricingData)));
        setIsDefaultMode(brandIdParam === "default");

        // Store override keys for highlighting brand-specific overrides
        if (response.overrideKeys && response.overrideKeys.length > 0) {
          setOverrideKeys(new Set(response.overrideKeys));
        } else {
          setOverrideKeys(new Set());
        }
      }
    } catch (err) {
      console.error('Error loading pricing data:', err);
      setLoadError('Failed to load pricing data. Using default values.');
    } finally {
      setLoadingData(false);
    }
  }, [brand?.id]);

  useEffect(() => {
    loadPricingData();
  }, [loadPricingData]);

  // EditableCell component with proper event handling
  const EditableCell = ({
    serviceId,
    weightRangeId,
    weightMin,
    weightMax,
    value,
    disabled: cellDisabled,
    overrideKeysSet,
    isDefault
  }) => {
    const cellKey = `${serviceId}-${weightRangeId}`;
    const isEditing = editingCell?.serviceId === serviceId && editingCell?.weightRangeId === weightRangeId;
    const tempValue = tempValues[cellKey] || value || '';
    const error = errors[cellKey];

    // Check if this cell is a brand-specific override (not in default mode)
    const overrideKey = `${serviceId}-${weightMin}-${weightMax}`;
    const isOverride = !isDefault && overrideKeysSet.has(overrideKey);

    const handleCellClick = useCallback((e) => {
      e.stopPropagation(); // Prevent IndexTable row selection
      if (!cellDisabled && !loading) {
        setEditingCell({ serviceId, weightRangeId });
        setTempValues(prev => ({ ...prev, [cellKey]: value || '' }));
      }
    }, [serviceId, weightRangeId, value, cellDisabled, cellKey]);

    const handleValueChange = useCallback((newValue) => {
      setTempValues(prev => ({ ...prev, [cellKey]: newValue || "" }));

      // Clear error when user starts typing
      if (error) {
        setErrors(prev => ({ ...prev, [cellKey]: null }));
      }
    }, [cellKey, error]);

    const validateAndSave = useCallback(() => {
      const trimmedValue = tempValues[cellKey].trim();

      // Allow empty values - treat as removing pricing for this weight/service
      if (!trimmedValue) {
        setPricingData(prev => ({
          ...prev,
          [serviceId]: {
            ...prev[serviceId],
            [weightRangeId]: ''
          }
        }));
        setHasChanges(true);
        setEditingCell(null);
        setErrors(prev => ({ ...prev, [cellKey]: null }));
        return;
      }

      const price = parseFloat(trimmedValue);

      // Validation for non-empty values
      if (isNaN(price) || price < 0) {
        setErrors(prev => ({ ...prev, [cellKey]: 'Enter a valid price' }));
        return;
      }

      if (price > 999.99) {
        setErrors(prev => ({ ...prev, [cellKey]: 'Price must be less than $999.99' }));
        return;
      }

      // Save the value
      setPricingData(prev => ({
        ...prev,
        [serviceId]: {
          ...prev[serviceId],
          [weightRangeId]: trimmedValue
        }
      }));

      setHasChanges(true);
      setEditingCell(null);
      setErrors(prev => ({ ...prev, [cellKey]: null }));
    }, [tempValues[cellKey], serviceId, weightRangeId, cellKey]);

    const handleBlur = useCallback(() => {

      validateAndSave();
    }, [validateAndSave]);

    const handleKeyPress = useCallback((e) => {

      if (e.key === 'Enter') {
        validateAndSave();
      } else if (e.key === 'Escape') {
        // Cancel editing
        setEditingCell(null);
        setTempValues(prev => ({ ...prev, [cellKey]: value || '' }));
        setErrors(prev => ({ ...prev, [cellKey]: null }));
      }
    }, [validateAndSave, cellKey, value]);

    if (isEditing) {
      return (
        <Box onClick={handleCellClick} style={{ maxWidth: '120px' }}>
          <TextField
            value={tempValues[cellKey]}
            onChange={handleValueChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyPress}
            prefix="$"
            type="number"
            step="0.01"
            min="0"
            max="999.99"
            placeholder="0.00"
            align="right"
            autoFocus
            selectTextOnFocus
            error={error}
            disabled={cellDisabled || loading}
            autoComplete="off"
          />
        </Box>
      );
    }

    return (
      <Box
        onClick={handleCellClick}
        style={{
          cursor: cellDisabled ? 'default' : 'pointer',
          minHeight: '44px',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: isOverride ? '#e3f2fd' : 'transparent',
          padding: isOverride ? '8px' : '0',
          borderRadius: isOverride ? '4px' : '0',
          margin: isOverride ? '-4px' : '0'
        }}
      >
        <Text
          variant="bodyMd"
          fontWeight="medium"
          tone={error ? 'critical' : value && parseFloat(value) > 0 ? 'base' : 'subdued'}
        >
          {value && parseFloat(value) > 0 ? formatCurrency(parseFloat(value)) : '—'}
        </Text>
        {error && (
          <Text variant="bodySm" tone="critical">
            {error}
          </Text>
        )}
      </Box>
    );
  };

  // Handle cancel - reload original data
  const handleCancel = useCallback(() => {
    loadPricingData(); // Reload from API
    setHasChanges(false);
    setEditingCell(null);
    setTempValues({});
    setErrors({});
  }, [loadPricingData]);

  // Handle bulk save - computes diff and sends only changed cells
  const handleSave = useCallback(async () => {
    if (onSave && hasChanges) {
      // Compute only changed cells
      const changes = [];
      Object.entries(pricingData).forEach(([serviceId, weightPrices]) => {
        Object.entries(weightPrices || {}).forEach(([weightRangeId, price]) => {
          const originalPrice = originalPricingData[serviceId]?.[weightRangeId] || '';
          console.log('originalPrice: ', originalPrice);
          console.log('price: ', price);
          console.log("_--------------------------------_");
          if (price !== originalPrice) {
            changes.push({
              serviceId,
              weightRangeId,
              oldValue: originalPrice,
              newValue: price
            });
          }
        });
      });

      // Also check for deletions (keys in original but not in current or empty in current)
      Object.entries(originalPricingData).forEach(([serviceId, weightPrices]) => {
        Object.entries(weightPrices || {}).forEach(([weightRangeId, originalPrice]) => {
          const currentPrice = pricingData[serviceId]?.[weightRangeId] || '';
          // If original had a value but current doesn't, and we haven't already tracked this change
          if (originalPrice && !currentPrice) {
            const alreadyTracked = changes.some(
              c => c.serviceId === serviceId && c.weightRangeId === weightRangeId
            );
            if (!alreadyTracked) {
              changes.push({
                serviceId,
                weightRangeId,
                oldValue: originalPrice,
                newValue: ''
              });
            }
          }
        });
      });

      try {
        await onSave(changes, pricingData);
        // Reload from API to get updated overrideKeys and fresh data
        await loadPricingData();
        setHasChanges(false);
      } catch (err) {
        console.error('Failed to save pricing data:', err);
      }
    }
  }, [onSave, pricingData, originalPricingData, hasChanges, loadPricingData]);

  // Handle reset to default - delete all brand overrides
  const handleResetToDefault = useCallback(async () => {
    if (!brand?.id || isDefaultMode) return;

    try {
      setResetting(true);
      const response = await apiClient.resetShippingFeesToDefault(brand.id);

      if (response.success) {
        setShowResetModal(false);
        // Reload data to show global defaults
        await loadPricingData();
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Failed to reset shipping fees:', error);
      setLoadError('Failed to reset shipping fees to default');
    } finally {
      setResetting(false);
    }
  }, [brand?.id, isDefaultMode, loadPricingData]);

  // IndexTable configuration
  const resourceName = {
    singular: 'weight range',
    plural: 'weight ranges',
  };

  const tableHeadings = [
    { title: 'Weights/Service', alignment: 'start' },
    { title: 'BoxC Parcel', alignment: 'start' },
    { title: 'BoxC Plus', alignment: 'start' },
    { title: 'BoxC Priority', alignment: 'start' },
  ];

  if (loading || loadingData) {
    return (
      <Box padding="400">
        <InlineStack align="center" gap="200">
          <Spinner size="large" />
          <Text>Loading shipping pricing table...</Text>
        </InlineStack>
      </Box>
    );
  }

  return (
    <BlockStack gap="400">
      {/* Error banner for data loading issues */}
      {loadError && (
        <Banner tone="critical" onDismiss={() => setLoadError(null)}>
          <Text>{loadError}</Text>
        </Banner>
      )}

      {/* Reset to Default button - only show for brand mode with overrides */}
      {!isDefaultMode && overrideKeys.size > 0 && (
        <InlineStack align="end">
          <Button
            variant="plain"
            tone="critical"
            onClick={() => setShowResetModal(true)}
          >
            Reset to Default ({overrideKeys.size} override{overrideKeys.size !== 1 ? 's' : ''})
          </Button>
        </InlineStack>
      )}

      {/* Reset confirmation modal */}
      <Modal
        open={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Reset Shipping Fees to Default"
        primaryAction={{
          content: 'Reset to Default',
          destructive: true,
          loading: resetting,
          onAction: handleResetToDefault,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowResetModal(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text>
              This will remove all {overrideKeys.size} custom shipping fee override{overrideKeys.size !== 1 ? 's' : ''} for this brand.
            </Text>
            <Text>
              After reset, this brand will use the global default shipping fees.
            </Text>
            <Text fontWeight="semibold" tone="caution">
              This action cannot be undone.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Header with save/cancel buttons */}
      {hasChanges && (
        <Banner tone="info">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="bodyMd" fontWeight="medium">Unsaved changes</Text>
            <InlineStack gap="400">
              <Button onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} loading={loading}>
                Save Changes
              </Button>
            </InlineStack>
          </InlineStack>
        </Banner>
      )}
      {/* Instructions */}
      <Box padding="300" background="bg-surface-info-subdued" borderRadius="200">
        <BlockStack gap="200">
          <Text variant="bodyMd" fontWeight="semibold">
            How to use this pricing table:
          </Text>
          <Text variant="bodySm">
            • Click on any price cell to edit the shipping fee for that weight range and service
          </Text>
          <Text variant="bodySm">
            • Press Enter to save or Escape to cancel editing. Leave empty to remove pricing.
          </Text>
          <Text variant="bodySm">
            • Changes are highlighted and require saving before taking effect
          </Text>
          {!isDefaultMode && (
            <Text variant="bodySm">
              • Cells with <span style={{ backgroundColor: '#e3f2fd', padding: '2px 6px', borderRadius: '4px' }}>blue background</span> are custom overrides for this brand. Other values use global defaults.
            </Text>
          )}
        </BlockStack>
      </Box>
      {/* Pricing Table */}
      <Box borderColor="border" borderWidth="025" borderRadius="200">
        <IndexTable
          resourceName={resourceName}
          itemCount={weightRanges.length}
          selectedItemsCount={0}
          onSelectionChange={() => { }} // No selection needed
          headings={tableHeadings}
          selectable={false}
          hasMoreItems={false}
          condensed={!smUp}
        >
          {weightRanges.map((weightRange, index) => (
            <IndexTable.Row
              id={weightRange.id}
              key={weightRange.id}
              position={index}
            >
              {/* Weight Range Column */}
              <IndexTable.Cell>
                <BlockStack gap="100">
                  <Text variant="bodyMd" fontWeight="semibold">
                    {weightRange.range}
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    {weightRange.kg}
                  </Text>
                </BlockStack>
              </IndexTable.Cell>

              {/* Service Price Columns */}
              {services.map((service) => (
                <IndexTable.Cell key={service.id}>
                  <EditableCell
                    serviceId={service.id}
                    weightRangeId={weightRange.id}
                    weightMin={weightRange.min}
                    weightMax={weightRange.max}
                    value={pricingData[service.id]?.[weightRange.id]}
                    disabled={disabled}
                    overrideKeysSet={overrideKeys}
                    isDefault={isDefaultMode}
                  />
                </IndexTable.Cell>
              ))}
            </IndexTable.Row>
          ))}
        </IndexTable>
      </Box>
    </BlockStack>
  );
};

export default ShippingPricingTable;